import {Button, CheckBox, Column, Row, RowLR, Select, Spinner, Text, TextInput, TimeSpanInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {ControlInput} from "../../../@Shared/ControlInput.js";
import {ES, InfoButton, RunInAction_Set, TextPlus} from "web-vcore";
import {AlarmUI} from "./Alarms/AlarmUI.js";
import {AlarmConfigBase, AlarmSequence, AlarmsGroup, AlarmType, GetAlarmSequenceFromAlarmGroup} from "../../../../Server/DB/engineConfigs/@EngineConfig/Alarms/@AlarmConfig.js";
import {EngineConfig_LightAlarm, EngineConfig_ScriptAlarm, EngineConfig_ShakeAlarm, EngineConfig_SoundAlarm} from "../../../../Server/DB/engineConfigs/@EngineConfig/Alarms/@EC_BasicAlarms.js";
import {EngineConfig_CountdownAlarm} from "../../../../Server/DB/engineConfigs/@EngineConfig/Alarms/@EC_CountdownAlarm.js";
import {colors} from "../../../../Utils/UI/GlobalStyles.js";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";
import {useState} from "react";
import {ShowMessageBox} from "react-vmessagebox";
import {store} from "../../../../Store/index.js";
import {Clone, GetEntries} from "js-vextensions";
import React from "react";
import {observer_mgl} from "mobx-graphlink";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {FieldLabel} from "../../../@Shared/FieldLabel.js";
import {Phase} from "../../../../Server/DB/@Shared/Phase.js";

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.alarms, (c, v)=>c.alarms = v);

const splitAt = 150;
export const PromptPanel_splitAt = splitAt;

export const Alarms_ConfigUI = observer_mgl((props: {remote?: boolean} & EngineConfigPanels_SharedProps)=>{
	const {client, enabled} = props;
	const sharedProps = GetSharedProps(props);
	const {config, ChangeConfig} = sharedProps;
	const fp = (fieldName: keyof typeof config)=>`alarms.${fieldName}`;

	return (
		<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5, color: colors.nightTint}}>
			<Row style={{fontSize: 18}}>
				<CheckBox text="Alarms" enabled={enabled} value={config.enabled} onChange={val=>ChangeConfig(c=>c.enabled = val)}/>
			</Row>

			{config.enabled &&
			<>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Voice, tag:" fieldPath={fp("voiceSoundTag")}/>
					<TextInput enabled={enabled} style={{width: 80}} value={config.voiceSoundTag} onChange={val=>ChangeConfig(c=>c.voiceSoundTag = val)}/>
					<FieldLabel ml={5} text="Vol. mult.:" fieldPath={fp("volumeMultiplier")}/>
					<SpinnerForPercent ml={5} enabled={enabled} min={0} max={500} value={config.volumeMultiplier} onChange={val=>ChangeConfig(c=>c.volumeMultiplier = val)}/>
				</RowLR>

				<Row mt={15} style={{fontSize: 18}}>Awakeness Signaling</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Manual trigger:</Text>
					<ControlInput enabled={enabled} value={config.signalAwakeness_triggerSet} onChange={val=>ChangeConfig(c=>c.signalAwakeness_triggerSet = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Other sources:</Text>
					<CheckBox text="Solve progress" enabled={enabled} value={config.signalAwakeness_solveProgress} onChange={val=>ChangeConfig(c=>c.signalAwakeness_solveProgress = val)}/>
					<CheckBox ml={10} text="Solve completion" enabled={false} value={true}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text>Effects:</Text>
					<InfoButton text={`
						If user enables "One-time skip-solve, if no awake-signal for X" feature (from live-session panel), this is involved.

						If a dreaming phase starts, and then awakeness is signalled, it does the following:
						* Creates a "General.SleepCycleEnd" event in active session.
						* Creates a "wake" segment in the session-associated journal-entry.
						(It then waits for next dreaming-phase start before enabling this trigger again.)
					`.AsMultiline(0)}/>
				</RowLR>

				<Row mt={15} style={{fontSize: 18}}>Phase: Drifting (off to sleep)</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<Text mr={5}>Enter effects:</Text>
					<Text>Sound:</Text>
					<TextInput ml={5} enabled={enabled} style={{width: 100}} value={config.phaseSleep_soundTag} onChange={val=>ChangeConfig(c=>c.phaseSleep_soundTag = val)}/>
					<Text ml={5}>Light:</Text>
					<TextInput ml={5} enabled={enabled} style={{width: 100}} value={config.phaseSleep_lightTag} onChange={val=>ChangeConfig(c=>c.phaseSleep_lightTag = val)}/>
				</RowLR>
				<Row mt={5} center>
					<CheckBox text="Switch to dreaming phase after delay" value={config.drift_alarmAfterDelay_enabled} onChange={val=>ChangeConfig(c=>c.drift_alarmAfterDelay_enabled = val)}/>
				</Row>
				{config.drift_alarmAfterDelay_enabled &&
				<Column ml={10}>
					<Row mt={5}>
						<Text mr={5}>Alarm delay (randomization pool):</Text>
						<TextInput enabled={enabled} value={config.alarmDelay_pool.join(", ")} onChange={valsStr=>{
							const newPool = [] as number[];
							for (const str of valsStr.split(",")) {
								const val = parseFloat(str);
								if (val != null && !isNaN(val)) {
									newPool.push(val);
								}
							}
							// don't allow user to make the pool empty
							if (newPool.length == 0) return;
							ChangeConfig(c=>c.alarmDelay_pool = newPool);
						}}/>
						<Text>m</Text>
					</Row>
					<Row mt={5}>
						<TextPlus info="For alarm-delays with no assignment here, the rate defaults to 1.">Pool rates:</TextPlus>
						<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>{
							ChangeConfig(c=>{
								for (let i = 0; i < 100; i++) {
									if (c.alarmDelay_poolRates[i] == null) {
										c.alarmDelay_poolRates[i] = 1;
										break;
									}
								}
							});
						}}/>
					</Row>
					{Object.entries(config.alarmDelay_poolRates).map(([alarmDelayStr, rate], index)=>{
						const alarmDelay = parseInt(alarmDelayStr);
						return (
							<Row key={index} ml={10} mt={5}>
								<Text>Delay:</Text>
								<Spinner ml={5} enabled={enabled} value={alarmDelay} onChange={newAlarmDelayForEntry=>ChangeConfig(c=>{
									// set the value this way, so entry ordering stays the same
									/*const newRatesMapObj = {};
									for (const [key, val] of Object.entries(c.alarmDelay_poolRates)) {
										if (key == alarmDelayStr) {
											newRatesMapObj[newAlarmDelayForEntry] = val;
										} else {
											newRatesMapObj[key] = val;
										}
									}
									c.alarmDelay_poolRates = newRatesMapObj;*/
									// firestore loses the ordering of map-objects anyway, so just use basic delete->set
									delete c.alarmDelay_poolRates[alarmDelay];
									c.alarmDelay_poolRates[newAlarmDelayForEntry] = rate;
								})}/>
								<Text ml={5}>Rate:</Text>
								<Spinner ml={5} enabled={enabled} value={rate} onChange={val=>ChangeConfig(c=>c.alarmDelay_poolRates[alarmDelay] = val)}/>
								<Button ml={5} mdIcon="delete" enabled={enabled} onClick={()=>{
									ChangeConfig(c=>delete c.alarmDelay_poolRates[alarmDelay]);
								}}/>
							</Row>
						);
					})}
					<Row mt={5}>
						<CheckBox enabled={enabled} text="Lock per session" value={config.alarmDelay_lockPerSession} onChange={val=>ChangeConfig(c=>c.alarmDelay_lockPerSession = val)}/>
						<InfoButton ml={5} text="If enabled, alarm-delay is calculated once at session start, rather than re-randomized each time a drifting-phase starts."/>
						<TextPlus ml={5} info={`
							1 = Every alarm-delay in the pool gets one draw-ticket, ie. an equal chance of being selected.
							2+ = Under-represented delay options get X draw-tickets, where X is the deficit of "nights used" compared to leader+1. (but limited to this setting)
							Note: The "nights used" value is the number of first-cycle dream-segments for which a given alarm-delay was used. (as can be seen on stats page)
						`.AsMultiline(0)}>Option catch-up rate:</TextPlus>
						<Spinner ml={5} enabled={enabled} min={1} step={1} value={config.alarmDelay_optionCatchUpRate} onChange={val=>ChangeConfig(c=>c.alarmDelay_optionCatchUpRate = val)}/>
					</Row>
				</Column>}

				<Row mt={15} style={{fontSize: 18}}>Phase: Dreaming</Row>
				<AlarmSequencesUI {...sharedProps} group="alarms"/>

				<Row mt={15} style={{fontSize: 18}}>Phase: Solving</Row>
				<RowLR mt={5} splitAt={splitAt}>
					<Text mr={5}>Enter effects:</Text>
					<Text>Sound:</Text>
					<TextInput ml={5} enabled={enabled} style={{width: 100}} value={config.phaseSolving_soundTag} onChange={val=>ChangeConfig(c=>c.phaseSolving_soundTag = val)}/>
					<Text ml={5}>Light:</Text>
					<TextInput ml={5} enabled={enabled} style={{width: 100}} value={config.phaseSolving_lightTag} onChange={val=>ChangeConfig(c=>c.phaseSolving_lightTag = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<Text mr={5}>Solve success, message:</Text>
					<TextInput enabled={enabled} value={config.solveSuccess_message} onChange={val=>ChangeConfig(c=>c.solveSuccess_message = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<TextPlus mr={5} info={`
						Note: The sound played is randomly selected from all sounds whose path includes the given text/subpath.
						Hint: To have no sound play on success, leave this empty.
						Hint: To have it play any alarm sound on your device, use "/".
					`.AsMultiline(0)}>Solve success, sound path:</TextPlus>
					<TextInput enabled={enabled} value={config.solveSuccess_soundPath} onChange={val=>ChangeConfig(c=>c.solveSuccess_soundPath = val)}/>
					<Text ml={5}>Volume:</Text>
					<Spinner ml={5} enabled={enabled} value={(config.solveSuccess_soundVolume * 100).RoundTo(.01)} onChange={val=>ChangeConfig(c=>c.solveSuccess_soundVolume = val / 100)} min={0} max={100}/>
					<Text>%</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt}>
					<FieldLabel text="Phase after solve-fail:" fieldPath={fp("phaseAfterSolveFail")} info={`
						Note: The micro-snooze comp can override this "phase afterward" setting, for its solving periods.
					`.AsMultiline(0)}/>
					<Select enabled={enabled} options={GetEntries(Phase, "ui")} value={config.phaseAfterSolveFail} onChange={val=>ChangeConfig(c=>c.phaseAfterSolveFail = val)}/>
				</RowLR>
			</>}
		</Column>
	);
});

export const AlarmSequencesUI = observer_mgl((props: {group: AlarmsGroup} & EngineConfigPanels_SharedProps)=>{
	const {group, enabled, client, config_full, ChangeConfig_Full} = props;

	//const sequences = group == "eeg" ? [config_full.eeg.alarmSequence] : config_full.alarms.phaseAlarm_sequences;
	const sequences = config_full.alarms.phaseAlarm_sequences;
	const [selectedSequenceIndex, setSelectedSequenceIndex] = useState(0);

	return (
		<>
			<Row mt={5}>
				<CheckBox enabled={enabled} text="Target sequence, lock per session" value={config_full.alarms.phaseAlarm_sequences_lockPerSession} onChange={val=>ChangeConfig_Full(c=>c.alarms.phaseAlarm_sequences_lockPerSession = val)}/>
			</Row>
			<Row mt={5}>
				<Text>Sequences:</Text>
				{sequences.map((sequence, index)=>{
					return <Button key={index} ml={5} p="2px 5px" text={`${index + 1}: ${sequence.name}${!sequence.enabled ? " [d]" : ""}`}
						style={ES(
							selectedSequenceIndex == index && {backgroundColor: "rgba(100,200,100,.5)"},
						)}
						onClick={()=>setSelectedSequenceIndex(index)}/>;
				})}
				<Button ml={5} mdIcon="plus" enabled={enabled && !client} onClick={()=>{
					ChangeConfig_Full(c=>{
						c.alarms.phaseAlarm_sequences.push(new AlarmSequence({enabled: true, alarms: []}));
					});
				}}/>
			</Row>
			{selectedSequenceIndex <= sequences.length - 1 &&
			<AlarmSequenceUI {...props} sequence={sequences[selectedSequenceIndex]} sequenceIndex={selectedSequenceIndex}/>}
		</>
	);
});

export const AlarmSequenceUI = observer_mgl((props: {group: AlarmsGroup, sequence: AlarmSequence, sequenceIndex: number} & EngineConfigPanels_SharedProps)=>{
	const {group, sequence, sequenceIndex, enabled, client, config_full, ChangeConfig_Full} = props;
	const uiState = store.main.engine.config;
	const ChangeSequence = (changerFunc: (sequence: AlarmSequence)=>void)=>{
		ChangeConfig_Full(c=>{
			const newSequence = GetAlarmSequenceFromAlarmGroup(group, c, sequenceIndex);
			changerFunc(newSequence);
		});
	};
	const ChangeAlarms = (alarmsChangerFunc: (alarms: AlarmConfigBase[])=>void)=>{
		ChangeSequence(s=>alarmsChangerFunc(s.alarms));
	};

	const sharedProps = props as EngineConfigPanels_SharedProps;
	return (
		<>
			{group != "eeg" &&
			<Row mt={5}>
				<Text>Name:</Text>
				<TextInput ml={5} enabled={enabled} value={sequence.name} onChange={val=>ChangeSequence(s=>s.name = val)}/>
				<CheckBox ml={5} text="Enabled" enabled={enabled} value={sequence.enabled} onChange={val=>ChangeSequence(s=>s.enabled = val)}/>
				<Button ml={5} mdIcon="content-copy" enabled={enabled && !client} onClick={()=>{
					RunInAction_Set(()=>uiState.alarms_copiedSequence = Clone(sequence));
				}}/>
				<Button ml={5} mdIcon="content-paste" enabled={enabled && !client} onClick={()=>{
					ShowMessageBox({
						title: "Replace sequence?", cancelButton: true,
						message: ()=><Column>
							<Row>Permanently replace this alarm-sequence with copied sequence?</Row>
							<Row>{`Copied sequence: "${uiState.alarms_copiedSequence?.name}"`}</Row>
						</Column>,
						onOK: ()=>{
							ChangeConfig_Full(c=>{
								c.alarms.phaseAlarm_sequences[sequenceIndex] = Clone(uiState.alarms_copiedSequence);
							});
						},
					});
				}}/>
				<Button ml={5} faIcon="arrow-left" title="Move left" enabled={enabled && sequenceIndex > 0} onClick={()=>{
					ChangeConfig_Full(c=>{
						const newSequences = c.alarms.phaseAlarm_sequences;
						newSequences.Move(newSequences[sequenceIndex], sequenceIndex - 1);
					});
				}}/>
				<Button ml={5} faIcon="arrow-right" title="Move right" enabled={enabled && sequenceIndex < config_full.alarms.phaseAlarm_sequences.length - 1} onClick={()=>{
					ChangeConfig_Full(c=>{
						const newSequences = c.alarms.phaseAlarm_sequences;
						newSequences.Move(newSequences[sequenceIndex], sequenceIndex + 1);
					});
				}}/>
				<Button ml={5} mdIcon="delete" enabled={enabled && !client} onClick={()=>{
					ShowMessageBox({
						title: "Delete sequence?", cancelButton: true,
						message: `Permanently delete this alarm-sequence?`,
						onOK: ()=>{
							ChangeConfig_Full(c=>{
								c.alarms.phaseAlarm_sequences.RemoveAt(sequenceIndex);
							});
						},
					});
				}}/>
			</Row>}
			<Row mt={5} mb={5}>
				<Text>Alarms:</Text>
				<Button ml={5} enabled={enabled && !client} text="Add sound" onClick={()=>{
					ChangeAlarms(a=>a.push(new EngineConfig_SoundAlarm({
						type: AlarmType.Sound,
					})));
				}}/>
				<Button ml={5} enabled={enabled && !client} text="Add countdown" onClick={()=>{
					ChangeAlarms(a=>a.push(new EngineConfig_CountdownAlarm({
						type: AlarmType.Countdown,
					})));
				}}/>
				<Button ml={5} enabled={enabled && !client} text="Add shake" onClick={()=>{
					ChangeAlarms(a=>a.push(new EngineConfig_ShakeAlarm({
						type: AlarmType.Shake,
					})));
				}}/>
				<Button ml={5} enabled={enabled && !client} text="Add light" onClick={()=>{
					ChangeAlarms(a=>a.push(new EngineConfig_LightAlarm({
						type: AlarmType.Light,
					})));
				}}/>
				<Button ml={5} enabled={enabled && !client} text="Add script" onClick={()=>{
					ChangeAlarms(a=>a.push(new EngineConfig_ScriptAlarm({
						type: AlarmType.Script,
					})));
				}}/>
			</Row>
			{sequence.alarms.map((prompt, index)=>{
				return <AlarmUI key={index} {...sharedProps} alarm={prompt} {...{index, group, sequence, sequenceIndex}}/>;
			})}
		</>
	);
});